import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Gift, DollarSign, CheckCircle, Clock, Share2, Users, ArrowRight, Copy, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const ReferralProgramPage = () => {
  const [copied, setCopied] = useState(false);
  const referralLink = 'https://socialdisputeco.com/?ref=YOUR_CODE';

  const copyToClipboard = () => {
    navigator.clipboard.writeText('https://socialdisputeco.com');
    setCopied(true);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const steps = [
    {
      icon: Share2,
      title: 'Share Your Link',
      description: 'Share Social Dispute Co with friends, family, or your audience who need help with suspended social media accounts.'
    },
    {
      icon: Users,
      title: 'They Sign Up & Pay',
      description: 'When your referral submits an intake form and purchases our Standard or Priority package, we track the referral.'
    },
    {
      icon: CheckCircle,
      title: 'Case Executed',
      description: 'Once we execute the case (prepare and submit documentation to the platform), your referral is confirmed.'
    },
    {
      icon: DollarSign,
      title: 'Get Paid $50',
      description: 'Receive $50 via Venmo within 30 days of case execution. Simple as that.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#1a1c1b]">
      <Header />
      
      <main className="pt-32 pb-16">
        <div className="max-w-5xl mx-auto px-5 md:px-10">
          
          {/* Hero Section */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#3f4816]/30 rounded-full mb-6">
              <Gift className="w-5 h-5 text-[#d9fb06]" />
              <span className="text-sm font-medium text-[#d9fb06] uppercase tracking-wide">Referral Program</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-[#d9fb06] uppercase leading-[0.9] mb-6">
              Earn $50 Per Referral
            </h1>
            <p className="text-lg md:text-xl text-[#888680] max-w-2xl mx-auto">
              Know someone struggling with a suspended social media account? 
              Help them organize their case and earn $50 for every successful referral.
            </p>
          </div>

          {/* Highlight Box */}
          <div className="bg-gradient-to-r from-[#3f4816]/50 to-[#302f2c] border border-[#d9fb06]/30 rounded-2xl p-8 mb-16">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-[#d9fb06] flex items-center justify-center">
                  <DollarSign className="w-8 h-8 text-[#1a1c1b]" />
                </div>
                <div>
                  <p className="text-3xl md:text-4xl font-black text-[#d9fb06]">$50</p>
                  <p className="text-[#888680]">Per Executed Referral</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <Clock className="w-6 h-6 text-[#d9fb06]" />
                <div>
                  <p className="text-lg font-bold text-[#dfddd6]">Paid via Venmo</p>
                  <p className="text-sm text-[#888680]">Within 30 days of case execution</p>
                </div>
              </div>
            </div>
          </div>

          {/* How It Works */}
          <div className="mb-16">
            <h2 className="text-2xl md:text-3xl font-black text-[#d9fb06] uppercase text-center mb-12">
              How It Works
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {steps.map((step, index) => (
                <div
                  key={step.title}
                  className="relative bg-[#302f2c] border border-[#3f4816]/50 rounded-xl p-6 text-center"
                >
                  <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-[#d9fb06] flex items-center justify-center">
                    <span className="text-sm font-bold text-[#1a1c1b]">{index + 1}</span>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-[#3f4816]/50 flex items-center justify-center mx-auto mb-4">
                    <step.icon className="w-6 h-6 text-[#d9fb06]" />
                  </div>
                  <h3 className="text-lg font-bold text-[#dfddd6] mb-2">{step.title}</h3>
                  <p className="text-sm text-[#888680]">{step.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Share Section */}
          <div className="bg-[#302f2c] border border-[#3f4816]/50 rounded-2xl p-8 mb-16">
            <h2 className="text-xl font-bold text-[#d9fb06] uppercase mb-6 text-center">
              Share Your Referral Link
            </h2>
            <div className="max-w-xl mx-auto">
              <div className="flex gap-3">
                <Input
                  value="https://socialdisputeco.com"
                  readOnly
                  className="bg-[#1a1c1b] border-[#3f4816] text-[#dfddd6] font-mono text-sm"
                />
                <Button
                  onClick={copyToClipboard}
                  className="bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90 font-bold px-6"
                  data-testid="copy-referral-link-btn"
                >
                  {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                </Button>
              </div>
              <p className="text-center text-sm text-[#888680] mt-4">
                When your referral contacts us, have them mention your name or email.
              </p>
            </div>
          </div>

          {/* Terms */}
          <div className="bg-[#1a1c1b] border border-[#3f4816]/30 rounded-xl p-8">
            <h2 className="text-xl font-bold text-[#d9fb06] uppercase mb-6">
              Referral Program Terms
            </h2>
            <ul className="space-y-3 text-[#888680] text-sm">
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#d9fb06] flex-shrink-0 mt-0.5" />
                <span>$50 commission paid per <strong className="text-[#dfddd6]">executed</strong> referral (case documentation submitted to platform)</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#d9fb06] flex-shrink-0 mt-0.5" />
                <span>Payment sent via <strong className="text-[#dfddd6]">Venmo</strong> within 30 days of case execution</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#d9fb06] flex-shrink-0 mt-0.5" />
                <span>Referral must be a <strong className="text-[#dfddd6]">new client</strong> who has not previously contacted Social Dispute Co</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#d9fb06] flex-shrink-0 mt-0.5" />
                <span>Referral must mention your name/email when they submit their intake form or during initial contact</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#d9fb06] flex-shrink-0 mt-0.5" />
                <span>No limit on the number of referrals you can make</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#d9fb06] flex-shrink-0 mt-0.5" />
                <span>You must provide a valid Venmo username to receive payment</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#d9fb06] flex-shrink-0 mt-0.5" />
                <span>Social Dispute Co reserves the right to modify or terminate this program at any time</span>
              </li>
            </ul>
          </div>

          {/* CTA */}
          <div className="text-center mt-12">
            <p className="text-[#888680] mb-4">
              Questions about the referral program?
            </p>
            <a
              href="mailto:intake@socialdisputeco.com?subject=Referral Program Question"
              className="inline-flex items-center gap-2 text-[#d9fb06] hover:underline font-medium"
            >
              Contact us at intake@socialdisputeco.com
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default ReferralProgramPage;
